import { afterNextRender, ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Toast } from '@shared/toast/toast';
import { NavComponent } from '@features/landing/components/nav/nav';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, NavComponent, Toast],
  templateUrl: './app.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App implements OnInit {

  private readonly titleService = inject(Title);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  /* The site nav is hidden on routes that carry `data: { nav: false }` (the auth pages bring their own frame). */
  protected readonly showNav = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.deepestRoute().snapshot.data['nav'] !== false),
    ),
    { initialValue: true },
  );

  constructor() {
    if (typeof window !== 'undefined' && 'scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    afterNextRender(() => {
      const nav = performance.getEntriesByType('navigation')[0] as
        | PerformanceNavigationTiming
        | undefined;
      if (nav?.type === 'reload') {
        if (window.location.hash) {
          history.replaceState(null, '', window.location.pathname + window.location.search);
        }
        window.scrollTo(0, 0);
      }
    });
  }

  ngOnInit(): void {
    this.titleService.setTitle(this.translate.instant('company.name'));
  }

  private deepestRoute(): ActivatedRoute {
    let current = this.route;
    while (current.firstChild) current = current.firstChild;
    return current;
  }

}
